import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
	CheckCircle2Icon,
	CircleAlertIcon,
} from "lucide-react";
import { PLATFORM_TO_MATRIX } from "./connections-api.js";
import type { ChatConnect } from "./messages.js";
import { COMMS_CHANNELS, CONNECTORS } from "./nav.js";
import { PlatformMark } from "./PlatformMark.js";

type Props = {
	connects: ChatConnect[];
};

function markIdForProvider(provider: string): string {
	const matrixId = PLATFORM_TO_MATRIX[provider] ?? provider;
	const fromList =
		CONNECTORS.find((row) => row.id === matrixId) ??
		COMMS_CHANNELS.find((row) => row.id === matrixId);
	return fromList?.markId ?? matrixId;
}

function statusBadge(row: ChatConnect) {
	switch (row.phase) {
		case "awaiting":
			return (
				<span className="inline-flex items-center gap-1 text-[12px] text-mx-quaternary">
					<Spinner className="size-3" />
					Waiting for Connect…
				</span>
			);
		case "connected":
			return (
				<span className="inline-flex items-center gap-1 text-[12px] text-emerald-500">
					<CheckCircle2Icon className="size-3.5" />
					Connected
				</span>
			);
		case "timed_out":
			return (
				<span className="inline-flex items-center gap-1 text-[12px] text-amber-500">
					<CircleAlertIcon className="size-3.5" />
					Timed out
				</span>
			);
		default: {
			const _exhaustive: never = row.phase;
			return _exhaustive;
		}
	}
}

/**
 * In-chat OAuth CTA — Cursor-style Connect button with brand marks.
 */
export function ConnectCards({ connects }: Props) {
	if (connects.length === 0) return null;

	return (
		<div className="mc-connects mb-2 flex w-full flex-col gap-2">
			{connects.map((row) => {
				const markId = markIdForProvider(row.provider);
				return (
					<div
						key={row.connectId}
						className="mc-connect flex flex-col gap-2 rounded-lg border border-mx-sep bg-mx-elevated/60 px-3 py-2.5"
					>
						<div className="flex items-center gap-2.5 text-sm">
							<span className="grid size-7 shrink-0 place-items-center rounded-md bg-mx-bg ring-1 ring-mx-sep">
								<PlatformMark
									markId={markId}
									size={16}
									fallback={row.label}
								/>
							</span>
							<span className="min-w-0 flex-1 truncate font-medium text-mx-label">
								{row.label}
							</span>
							{statusBadge(row)}
						</div>
						{row.reason ? (
							<p className="m-0 text-[12.5px] leading-snug text-mx-secondary">
								{row.reason}
							</p>
						) : null}
						{row.phase === "connected" && row.portfolioCount != null ? (
							<p className="m-0 text-[12px] text-mx-tertiary">
								{row.portfolioCount} resource
								{row.portfolioCount === 1 ? "" : "s"} in portfolio
							</p>
						) : null}
						{(row.phase === "awaiting" || row.phase === "timed_out") &&
						row.authorizeUrl ? (
							<Button
								type="button"
								size="sm"
								className="h-8 w-fit gap-1.5"
								onClick={() => {
									const popup = window.open(
										row.authorizeUrl,
										"mairko-oauth",
										"popup=yes,width=520,height=720",
									);
									if (!popup) {
										window.location.href = row.authorizeUrl!;
									}
								}}
							>
								<PlatformMark
									markId={markId}
									size={14}
									fallback={row.label}
								/>
								Connect {row.label}
							</Button>
						) : null}
					</div>
				);
			})}
		</div>
	);
}
